import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, from, map, Observable, of, switchMap, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocationFilter, Location, MoonShared } from '@moon-v2/moon-share';
import { PagedData } from '@shared/models/pagination-model';
import { TtlSessionCache } from '@shared/helpers/ttl-session-cache';

const MS_LOCATIONS_URL = `${environment.baseUrl}/ms-locations/locations`;
const CACHE_TTL_MS = 5 * 60 * 1000;
const LIST_CACHE_TTL_MS = 30 * 1000;
const LIST_CACHE_STORAGE_PREFIX = 'moon.locationListCache:';

/** Champs demandés sur chaque ancêtre quand l'appelant réclame une projection imbriquée. */
const PARENT_PROJECTION = ['name', 'level', 'uuid'];

/**
 * Clés de projection imbriquée sur les ancêtres, syntaxe API Platform :
 * `fields[parent][]`, puis `fields[parent][parent][]`, etc.
 *
 * ⚠️ **Les crochets finaux sont obligatoires.** Sans eux, l'API ignore la clé en silence et
 * `parent` revient en simple IRI (`/locations/<uuid>`) : le code reçoit une chaîne là où il
 * attend un objet, et la colonne Parent s'affiche vide sans la moindre erreur. C'est la
 * graphie de l'ancienne webapp (`buildParentFieldsFilter`), qui fait autorité —
 * `LocationFilter` ne déclare pas ces clés, le backend les honore malgré tout.
 *
 * Fonction à part, et exportée, parce que `getAll` n'est pas testable ici (cf. l'entête du
 * spec) : c'est le seul moyen de verrouiller cette graphie.
 */
export function parentProjectionParams(depth: number): Record<string, string[]> {
  const params: Record<string, string[]> = {};
  let key = 'fields';
  for (let i = 0; i < depth; i++) {
    key += '[parent]';
    params[`${key}[]`] = PARENT_PROJECTION;
  }
  return params;
}

type MoonParams = Record<string, unknown>;

interface MoonRawResult {
  items: Location[];
  totalItems: number;
}

@Injectable({ providedIn: 'root' })
export class LocationService {
  private readonly http = inject(HttpClient);

  private readonly cache = new Map<string, { data: Location; timestamp: number }>();

  // Cache des listes (getAll/getForMap/getAllAtLevel) : évite de re-fetcher toutes les
  // pages à chaque refresh de la carte. Logique extraite dans TtlSessionCache (générique,
  // testable indépendamment de MoonShared).
  private readonly listCache = new TtlSessionCache<PagedData<Location>>(LIST_CACHE_STORAGE_PREFIX);

  private isCacheValid(key: string): boolean {
    const entry = this.cache.get(key);
    return !!entry && Date.now() - entry.timestamp < CACHE_TTL_MS;
  }

  clearCache(): void {
    this.cache.clear();
    this.listCache.clear();
  }

  private toPagedData(value: unknown): PagedData<Location> {
    const raw = value as MoonRawResult;
    return { data: raw.items, paginator: { totalElements: raw.totalItems } };
  }

  getAllDirect(token: string, filter: Record<string, string>): Observable<PagedData<Location>> {
    const params = new HttpParams({ fromObject: filter });
    return this.http
      .get<{ member: Location[]; totalItems: number }>(MS_LOCATIONS_URL, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      })
      .pipe(
        map((value) => ({
          data: value.member,
          paginator: { totalElements: value.totalItems },
        })),
      );
  }

  getAll(filter: LocationFilter, parents: number | undefined): Observable<PagedData<Location>> {
    const queryParams: MoonParams = { ...filter } as MoonParams;

    if (parents) {
      Object.assign(queryParams, parentProjectionParams(parents));
      // La projection du niveau 0 n'est posée QUE si l'appelant n'a pas la sienne. L'ancienne
      // implémentation l'écrasait : demander les parents faisait perdre `active` et les
      // coordonnées, ce qui dissuadait d'utiliser ce paramètre.
      queryParams['fields[]'] ??= PARENT_PROJECTION;
    }

    // TTL long (5 min, comme get(uuid)) uniquement pour les checks structurels (volumétrie
    // par niveau pour les sélecteurs en cascade, résolution de parents...), qui varient
    // quasiment jamais entre deux refresh. Dès qu'un terme de recherche libre est présent
    // (typeahead), on retombe sur le TTL court : une location créée/renommée par un autre
    // agent doit réapparaître dans la recherche rapidement, pas jusqu'à 5 min plus tard.
    const hasFreeTextSearch = !!(queryParams as { name?: unknown }).name;
    const ttlMs = hasFreeTextSearch ? LIST_CACHE_TTL_MS : CACHE_TTL_MS;

    return this.listCache.get(
      { op: 'getAll', queryParams, parents },
      () =>
        from(
          MoonShared.locationService.findAll(queryParams as LocationFilter, {
            inLocation: filter.inLocation,
          }),
        ).pipe(map((value) => this.toPagedData(value))),
      ttlMs,
    );
  }

  getAllAtLevel(inLocation: string, level: string): Observable<PagedData<Location>> {
    return this.listCache.get(
      { op: 'getAllAtLevel', inLocation, level },
      () =>
        this.fetchAllPages(
          { inLocation, level, 'fields[]': ['uuid', 'name', 'level'] } as unknown as LocationFilter,
          inLocation,
        ),
      LIST_CACHE_TTL_MS,
    );
  }

  getForMap(
    inLocation: string | string[] | undefined,
    withParent = false,
  ): Observable<PagedData<Location>> {
    if (!inLocation || (Array.isArray(inLocation) && inLocation.length === 0)) {
      return of({ data: [], paginator: { totalElements: 0 } });
    }
    // context.inLocation (2e arg de findAll, cf. fetchAllPages) ne supporte qu'une valeur
    // unique côté MoonShared — non transmis pour un scope multi-parents (fallback sur le
    // scope racine par défaut), seul le filtre `inLocation[]` porte la restriction réelle.
    const singleInLocation = Array.isArray(inLocation) ? undefined : inLocation;
    const filter: Record<string, unknown> = {
      ...(Array.isArray(inLocation) ? { 'inLocation[]': inLocation } : { inLocation }),
      'fields[]': ['uuid', 'name', 'latitude', 'longitude', 'active', 'level', 'population'],
    };
    if (withParent) {
      filter['fields[parent][]'] = ['uuid', 'level'];
    }
    return this.listCache.get(
      { op: 'getForMap', inLocation, withParent, filter },
      () => this.fetchAllPages(filter as unknown as LocationFilter, singleInLocation),
      LIST_CACHE_TTL_MS,
    );
  }

  private fetchAllPages(
    filter: LocationFilter,
    inLocation: string | undefined,
    pageSize = 200,
  ): Observable<PagedData<Location>> {
    const page1: MoonParams = { ...(filter as MoonParams), itemsPerPage: pageSize, page: 1 };
    return from(MoonShared.locationService.findAll(page1 as LocationFilter, { inLocation })).pipe(
      switchMap((first) => {
        const total = (first as unknown as MoonRawResult).totalItems;
        const pages = Math.ceil(total / pageSize);
        if (pages <= 1) return of(this.toPagedData(first));

        const rest$ = Array.from({ length: pages - 1 }, (_, i) => {
          const p: MoonParams = { ...(filter as MoonParams), itemsPerPage: pageSize, page: i + 2 };
          return from(MoonShared.locationService.findAll(p as LocationFilter, { inLocation }));
        });

        return forkJoin(rest$).pipe(
          map((results) => ({
            data: [
              ...(first as unknown as MoonRawResult).items,
              ...results.flatMap((r) => (r as unknown as MoonRawResult).items),
            ],
            paginator: { totalElements: total },
          })),
        );
      }),
    );
  }

  get(uuid: string, fields?: string[]): Observable<Location> {
    if (!fields && this.isCacheValid(uuid)) {
      return of(this.cache.get(uuid)!.data);
    }
    const params = fields?.length ? ({ 'fields[]': fields } as LocationFilter) : undefined;
    return from(MoonShared.locationService.find(uuid, params as LocationFilter)).pipe(
      tap((data) => {
        if (!fields) this.cache.set(uuid, { data: data as Location, timestamp: Date.now() });
      }),
      map((value) => value as Location),
    );
  }

  getByUuids(uuids: string[], fields?: string[]): Observable<Location[]> {
    if (!uuids.length) return of([]);
    return forkJoin(
      uuids.map((uuid) => this.get(uuid, fields).pipe(catchError(() => of(null)))),
    ).pipe(map((results) => results.filter((r): r is Location => r !== null)));
  }

  // ── Mutations ────────────────────────────────────────────────────────────

  create(data: Partial<Location>): Observable<Location> {
    return from(MoonShared.locationService.create(data)).pipe(
      tap(() => this.clearCache()),
      map((value) => value as Location),
    );
  }

  update(uuid: string, data: Partial<Location>): Observable<Location> {
    return from(MoonShared.locationService.patch(uuid, data)).pipe(
      tap(() => this.clearCache()),
      map((value) => value as Location),
    );
  }

  delete(uuid: string): Observable<void> {
    return from(MoonShared.locationService.delete(uuid)).pipe(tap(() => this.clearCache()));
  }

  activate(uuid: string): Observable<Location> {
    return from(MoonShared.locationService.activate(uuid)).pipe(
      tap(() => this.clearCache()),
      map((value) => value as Location),
    );
  }

  deactivate(uuid: string): Observable<Location> {
    return from(MoonShared.locationService.deactivate(uuid)).pipe(
      tap(() => this.clearCache()),
      map((value) => value as Location),
    );
  }

  // ── Hiérarchie ───────────────────────────────────────────────────────────

  // N'utilise pas `getAll` (donc pas le cache TTL) : une réponse vide transitoire
  // (RBAC pas encore hydraté, token en cours de refresh...) serait sinon mise en cache
  // comme un résultat valide et resservie pendant CACHE_TTL_MS, bloquant la résolution
  // de la location utilisateur jusqu'à expiration du cache ou vidage manuel du storage.
  /**
   * `scope` : le périmètre envoyé en contexte au SDK. Absent, le SDK retombe sur sa
   * configuration globale — le PAYS sélectionné — et un emplacement hors de ce pays revient
   * vide. Une FICHE passe donc l'uuid de l'emplacement lui-même : il est forcément dans son
   * propre sous-arbre, et la réponse ne dépend plus du sélecteur de pays.
   */
  getAllParentLocations(uuid: string, depth = 7, scope?: string): Observable<Location[]> {
    const queryParams: MoonParams = { uuid };
    let parentKey = '';
    for (let i = 0; i <= depth; i++) {
      if (i > 0) parentKey += '[parent]';
      queryParams[`fields${parentKey}`] = ['name', 'level', 'uuid'];
    }

    return from(
      MoonShared.locationService.findAll(
        queryParams as LocationFilter,
        scope ? { inLocation: scope } : {},
      ),
    ).pipe(
      map((value) => this.toPagedData(value)),
      map((result) => {
        if (!result.data?.length) return [];
        const chain: Location[] = [];
        let current: (Location & { parent?: Location }) | null = result.data[0] as Location & {
          parent?: Location;
        };
        while (current) {
          chain.unshift(current);
          current = current.parent ?? null;
        }
        return chain;
      }),
      catchError(() => of([])),
    );
  }
}
